<template>
  <v-menu
    v-model="menuOpen"
    :close-on-content-click="false"
    location="bottom end"
    offset="10"
  >
    <template v-slot:activator="{ props }">
      <v-btn
        icon
        v-bind="props"
        class="mr-2"
      >
        <v-badge
          v-if="unreadCount > 0"
          :content="unreadCount > 99 ? '99+' : unreadCount"
          color="error"
        >
          <v-icon>mdi-bell</v-icon>
        </v-badge>
        <v-icon v-else>mdi-bell-outline</v-icon>
      </v-btn>
    </template>

    <v-card width="400" max-height="500">
      <v-card-title class="d-flex justify-space-between align-center py-2">
        <span>Notifications</span>
        <div>
          <v-btn
            v-if="unreadCount > 0"
            variant="text"
            size="small"
            @click="markAllRead"
            :loading="markingAllRead"
          >
            Mark all read
          </v-btn>
          <v-btn icon size="small" @click="refreshNotifications" :loading="loading">
            <v-icon size="small">mdi-refresh</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-0" style="max-height: 400px; overflow-y: auto;">
        <!-- Loading state -->
        <div v-if="loading && notifications.length === 0" class="text-center py-4">
          <v-progress-circular indeterminate size="24"></v-progress-circular>
        </div>

        <!-- Empty state -->
        <div v-else-if="notifications.length === 0" class="text-center py-8 text-grey">
          <v-icon size="48" color="grey-lighten-1">mdi-bell-off-outline</v-icon>
          <p class="mt-2">No notifications</p>
        </div>

        <!-- Notifications list -->
        <v-list v-else density="compact" class="py-0">
          <template v-for="(notification, index) in notifications" :key="notification.id">
            <v-list-item
              :class="{ 'bg-blue-lighten-5': !notification.read }"
              @click="openNotificationDetails(notification)"
            >
              <template v-slot:prepend>
                <v-icon :color="getNotificationColor(notification)" size="small">
                  {{ getNotificationIcon(notification) }}
                </v-icon>
              </template>

              <v-list-item-title class="text-body-2 font-weight-medium">
                {{ notification.title }}
              </v-list-item-title>
              <v-list-item-subtitle class="text-caption">
                {{ notification.message }}
              </v-list-item-subtitle>
              <v-list-item-subtitle class="text-caption text-grey">
                {{ formatTime(notification.created_at) }}
              </v-list-item-subtitle>

              <template v-slot:append>
                <v-btn
                  v-if="!notification.read"
                  icon
                  size="x-small"
                  variant="text"
                  @click.stop="markAsRead(notification)"
                >
                  <v-icon size="small">mdi-check</v-icon>
                </v-btn>
              </template>
            </v-list-item>
            <v-divider v-if="index < notifications.length - 1"></v-divider>
          </template>
        </v-list>
      </v-card-text>
    </v-card>
  </v-menu>

  <!-- Notification Details Dialog -->
  <v-dialog v-model="showDetailsDialog" max-width="600">
    <v-card v-if="selectedNotification">
      <v-card-title class="d-flex align-center">
        <v-icon :color="getNotificationColor(selectedNotification)" class="mr-2">
          {{ getNotificationIcon(selectedNotification) }}
        </v-icon>
        {{ selectedNotification.title }}
      </v-card-title>

      <v-card-text>
        <p class="mb-4">{{ selectedNotification.message }}</p>

        <!-- Bulk purchase results -->
        <div v-if="selectedNotification.notification_type === 'bulk_purchase_result' && selectedNotification.data">
          <v-divider class="mb-4"></v-divider>

          <!-- Summary -->
          <div class="d-flex gap-4 mb-4">
            <v-chip color="success" size="small">
              <v-icon start size="small">mdi-check</v-icon>
              {{ selectedNotification.data.successful?.length || 0 }} Successful
            </v-chip>
            <v-chip color="error" size="small">
              <v-icon start size="small">mdi-close</v-icon>
              {{ selectedNotification.data.failed?.length || 0 }} Failed
            </v-chip>
          </div>

          <!-- Order Info & Payment Button -->
          <div v-if="selectedNotification.data.order_id" class="mb-4">
            <v-card variant="tonal" color="primary" class="pa-3">
              <div class="d-flex justify-space-between align-center">
                <div>
                  <div class="text-subtitle-2">Order #{{ selectedNotification.data.order_id }}</div>
                  <div class="text-caption">{{ selectedNotification.data.successful?.length || 0 }} MSISDNs</div>
                </div>
                <v-btn
                  v-if="selectedNotification.data.payment_url"
                  color="primary"
                  variant="elevated"
                  @click="closeAfterPayClick"
                >
                  <v-icon start>mdi-credit-card</v-icon>
                  Pay Now
                </v-btn>
                <v-chip v-else color="success" size="small">
                  <v-icon start size="small">mdi-check</v-icon>
                  Free / No Payment
                </v-chip>
              </div>
            </v-card>
          </div>

          <!-- Successful items -->
          <div v-if="selectedNotification.data.successful?.length > 0" class="mb-4">
            <div class="text-subtitle-2 mb-2 text-success">Successful MSISDNs:</div>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>MSISDN</th>
                  <th>Service ID</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in selectedNotification.data.successful" :key="item.msisdn_id">
                  <td>{{ item.msisdn }}</td>
                  <td>{{ item.client_service_id }}</td>
                </tr>
              </tbody>
            </v-table>
          </div>

          <!-- Failed items -->
          <div v-if="selectedNotification.data.failed?.length > 0">
            <div class="text-subtitle-2 mb-2 text-error">Failed:</div>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>MSISDN</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in selectedNotification.data.failed" :key="item.msisdn_id">
                  <td>{{ item.msisdn || item.msisdn_id }}</td>
                  <td class="text-error">{{ item.error }}</td>
                </tr>
              </tbody>
            </v-table>
          </div>
        </div>

        <div class="text-caption text-grey mt-4">
          {{ formatFullTime(selectedNotification.created_at) }}
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" color="red" @click="showDetailsDialog = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent } from 'vue';
import { log } from "@/composables/useLog";
import store from "@/store";
import { getNotifications, markNotificationRead, markAllNotificationsRead } from "@/composables/useNotificationsAPI";

export default defineComponent({
  name: 'NotificationsPanel',
  data() {
    return {
      menuOpen: false,
      loading: false,
      notifications: [],
      unreadCount: 0,
      token: '',

      // Mark all read
      markingAllRead: false,

      // Details dialog
      showDetailsDialog: false,
      selectedNotification: null,

      // Polling interval
      pollInterval: null,
      pollingIntervalMs: 0 // Disabled by default, can be set from localStorage
    }
  },
  methods: {
    async loadNotifications() {
      this.loading = true;
      try {
        const response = await getNotifications(this.token, 1, 50, false);
        if (!response.error) {
          this.notifications = response.notifications || [];
          this.unreadCount = response.unread_count || 0;
        }
      } catch (err) {
        log("Error loading notifications:");
        log(err);
      } finally {
        this.loading = false;
      }
    },

    async refreshNotifications() {
      await this.loadNotifications();
    },

    async markAsRead(notification) {
      try {
        const response = await markNotificationRead(this.token, notification.id);
        if (!response.error) {
          notification.read = true;
          notification.read_at = new Date().toISOString();
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        }
      } catch (err) {
        log("Error marking notification read:");
        log(err);
      }
    },

    async markAllRead() {
      this.markingAllRead = true;
      try {
        const response = await markAllNotificationsRead(this.token);
        if (!response.error) {
          this.notifications.forEach(n => {
            n.read = true;
            n.read_at = new Date().toISOString();
          });
          this.unreadCount = 0;
        }
      } catch (err) {
        log("Error marking all notifications read:");
        log(err);
      } finally {
        this.markingAllRead = false;
      }
    },

    openNotificationDetails(notification) {
      // Mark as read when opening
      if (!notification.read) {
        this.markAsRead(notification);
      }
      this.selectedNotification = notification;
      this.showDetailsDialog = true;
      this.menuOpen = false;
    },

    getNotificationIcon(notification) {
      const icons = {
        'bulk_purchase_result': 'mdi-cart-check',
        'success': 'mdi-check-circle',
        'warning': 'mdi-alert',
        'error': 'mdi-alert-circle',
        'info': 'mdi-information'
      };
      return icons[notification.notification_type] || 'mdi-bell';
    },

    getNotificationColor(notification) {
      if (notification.status === 'failed') return 'error';
      if (notification.status === 'partial_success') return 'warning';
      if (notification.status === 'completed') return 'success';
      if (notification.status === 'pending') return 'info';

      const colors = {
        'success': 'success',
        'warning': 'warning',
        'error': 'error',
        'info': 'info'
      };
      return colors[notification.notification_type] || 'primary';
    },

    formatTime(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    },

    formatFullTime(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleString();
    },

    loadPollingInterval() {
      const savedInterval = localStorage.getItem('notificationPollingInterval');
      if (savedInterval !== null) {
        this.pollingIntervalMs = parseInt(savedInterval, 10);
      }
      log("Notification polling interval: " + this.pollingIntervalMs + "ms");
    },

    startPolling() {
      // Don't poll if interval is 0 (disabled)
      if (this.pollingIntervalMs <= 0) {
        log("Notification polling is disabled");
        return;
      }
      this.pollInterval = setInterval(() => {
        this.loadNotifications();
      }, this.pollingIntervalMs);
      log("Started notification polling every " + this.pollingIntervalMs + "ms");
    },

    restartPolling() {
      this.stopPolling();
      this.loadPollingInterval();
      this.startPolling();
    },

    handlePreferencesUpdated(event) {
      if (event.detail && event.detail.notificationPollingInterval !== undefined) {
        log("Preferences updated, restarting polling with new interval");
        this.restartPolling();
      }
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },

    handlePaymentComplete() {
      // Refresh notifications when payment completes
      this.loadNotifications();
    },

    closeAfterPayClick() {
      const paymentUrl = this.selectedNotification?.data?.payment_url;
      const orderId = this.selectedNotification?.data?.order_id;
      // Close the notification details dialog
      this.showDetailsDialog = false;
      this.menuOpen = false;
      // Dispatch event to show payment popup in MsisdnsList
      window.dispatchEvent(new CustomEvent('show-payment-popup', {
        detail: {
          url: paymentUrl,
          message: `Order #${orderId} requires payment. Click below to complete your purchase.`
        }
      }));
    }
  },
  mounted() {
    this.token = store.getters.getToken;
    this.loadPollingInterval();
    if (this.token && this.pollingIntervalMs > 0) {
      this.loadNotifications();
      this.startPolling();
    }
    // Listen for payment complete events to refresh notifications
    window.addEventListener('payment-complete', this.handlePaymentComplete);
    // Listen for preferences updates to restart polling
    window.addEventListener('preferences-updated', this.handlePreferencesUpdated);
  },
  beforeUnmount() {
    this.stopPolling();
    window.removeEventListener('payment-complete', this.handlePaymentComplete);
    window.removeEventListener('preferences-updated', this.handlePreferencesUpdated);
  },
  watch: {
    menuOpen(val) {
      if (val) {
        // Refresh when menu opens
        this.loadNotifications();
      }
    }
  }
});
</script>

<style scoped>
.gap-4 {
  gap: 16px;
}
</style>
